import axios from "axios";
import store from "./../redux/store/store.js";
import { logoutUser } from "./../redux/reducer/userSlice.js";
import { showToast } from "./../redux/reducer/utilsSlice.js";

// Resolve API base URL from .env. `REACT_APP_BACKEND_URL` is the single
// source of truth for which backend to talk to — set in .env. Empty falls
// back to "/api" so Vercel's rewrite handles routing (same-origin, no CORS).
// CRA inlines env vars at build time, so a redeploy is needed after edits.
const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const baseURL = backendUrl ? `${backendUrl}/api` : "/api";

export const instance = axios.create({
  baseURL: baseURL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "fetch",
  },
  withCredentials: true,
});

function extractBackendMessage(error) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim().length > 0 && data.trim().length < 300) {
    return data.trim();
  }
  if (data && typeof data === "object") {
    return data.error || data.message || null;
  }
  return null;
}

function friendlyMessage(error) {
  const status = error?.response?.status;
  const backendMsg = extractBackendMessage(error);
  if (status === 402) {
    return backendMsg || "Subscription required to continue.";
  }
  if (status === 413) {
    return "File or request is too large.";
  }
  if (status === 429) {
    return backendMsg || "Too many requests. Please slow down and try again.";
  }
  if (status >= 500) {
    return "Something went wrong. Please try again in a moment.";
  }
  if (error?.code === "ECONNABORTED") {
    return "Request timed out. Check your connection and try again.";
  }
  if (error?.message === "Network Error") {
    return "No internet connection.";
  }
  return backendMsg || error?.message || "Something went wrong";
}

/**
 * Make API request with automatic token handling
 * @param {string} type - HTTP method (GET, POST, PUT, PATCH, DELETE)
 * @param {string} path - API endpoint path
 * @param {object|FormData} body - Request body
 * @param {object} options - Additional axios options
 */
export const makeRequest = async (type, path, body = null, options = {}) => {
  try {
    const token = store.getState().user.userToken;

    const headers = {
      ...options.headers,
      authorization: token ? `Bearer ${token}` : null,
    };

    if (body instanceof FormData) {
      headers["Content-Type"] = "multipart/form-data";
    } else if (body) {
      headers["Content-Type"] = "application/json";
    }

    const config = {
      timeout: 30000,
      headers,
      ...options,
    };

    let response;

    switch (type.toUpperCase()) {
      case "GET":
        response = await instance.get(path, {
          ...config,
          params: options.params || {},
        });
        break;
      case "POST":
        response = await instance.post(path, body, config);
        break;
      case "PUT":
        response = await instance.put(path, body, config);
        break;
      case "PATCH":
        response = await instance.patch(path, body, config);
        break;
      case "DELETE":
        response = await instance.delete(path, config);
        break;
      default:
        throw new Error("Unsupported request type");
    }
    return response;
  } catch (error) {
    // Interceptor has already toasted (and cleared auth on 401).
    throw error;
  }
};

// Request interceptor for adding authentication token
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling responses and errors
instance.interceptors.response.use(
  (response) => {
    if (response.data?.data && (response.data?.page !== undefined || response.data?.pagination)) {
      return {
        ...response.data,
        items: response.data.data,
      };
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data;
  },
  async (error) => {
    const status = error?.response?.status;
    const message = friendlyMessage(error);
    const requestUrl = error?.config?.url || "";
    // A failed login is its own thing — the login screen shows a precise
    // "invalid credentials" message. Don't hijack it with a generic toast or
    // a "session expired" logout (there's no session yet).
    const isLoginRequest = requestUrl.includes("auth/login");

    if (isLoginRequest) {
      // handled by the login screen
    } else if (status === 401) {
      // Only an invalid/expired session logs the user out. A 403 is a
      // permission boundary (e.g. a mall admin touching a super-admin-only
      // endpoint) — nuking the whole session for it would log out users
      // who simply lack a permission.
      localStorage.removeItem("token");
      store.dispatch(logoutUser());
      store.dispatch(showToast({ message: "Session expired. Please log in again", type: "error" }));
    } else if (status === 403) {
      store.dispatch(showToast({ message: message || "You don't have permission to do that", type: "error" }));
    } else {
      store.dispatch(showToast({ message, type: "error" }));
    }

    return Promise.reject({
      ...error,
      message,
      status,
      data: error?.response?.data,
      subscriptionRequired: status === 402,
      rateLimited: status === 429,
      payloadTooLarge: status === 413,
      serverError: status >= 500,
    });
  }
);
